import { SERVER_SUPPORTS } from './serverSupport';
import type {
  ColumnRule,
  DoNotMatchOverrides,
  MatchingConfig,
  MatchingSessionState,
} from './types';

export function emptyDoNotMatch(): DoNotMatchOverrides {
  return { added: [], removed: [], removedUnrecognized: {} };
}

/**
 * Configs saved before do-not-match became reflexive pairs stored a full
 * list per person. Those lists can't be read as pairs, so they start over.
 */
export function upgradeConfig(config: MatchingConfig): MatchingConfig {
  const dnm = config.participants?.doNotMatch as
    | Partial<DoNotMatchOverrides>
    | undefined;
  if (
    dnm &&
    Array.isArray(dnm.added) &&
    Array.isArray(dnm.removed) &&
    dnm.removedUnrecognized
  )
    return config;
  return {
    ...config,
    participants: { ...config.participants, doNotMatch: emptyDoNotMatch() },
  };
}

/** False when the session has no coaches, so coach capacity can't be enforced. */
export function hasCoachData(session: MatchingSessionState): boolean {
  return session.coaches.length > 0;
}

/**
 * Fit a saved config to the session it belongs to and to what matching-server
 * reads, so an older config can't carry a setting that can't take effect:
 * - Without coach data, coach capacity can't be enforced (the run could never
 *   succeed), so it is forced off.
 * - Controls hidden until the server reads them (`SERVER_SUPPORTS`) are
 *   pinned: participant availability stays enforced, and the maximum number
 *   of types in a spread goal stays at no limit.
 */
export function fitConfigToSession(
  config: MatchingConfig,
  hasCoaches: boolean
): MatchingConfig {
  let next = config;
  if (!hasCoaches && next.enforce.coachCapacity)
    next = { ...next, enforce: { ...next.enforce, coachCapacity: false } };
  if (!SERVER_SUPPORTS.availabilitySwitch && !next.enforce.availability)
    next = { ...next, enforce: { ...next.enforce, availability: true } };
  if (
    !SERVER_SUPPORTS.typesMax &&
    next.goals.some(g => g.how === 'spread' && g.types.max !== null)
  )
    next = {
      ...next,
      goals: next.goals.map(g =>
        g.how === 'spread' && g.types.max !== null
          ? { ...g, types: { ...g.types, max: null } }
          : g
      ),
    };
  return next;
}

/**
 * SGOS columns that name a participant's manager, in order of preference.
 * Cohorts fill one or both; the session only has a column when it has data.
 */
export const MANAGER_COLUMNS = ['manager', 'manager_email'];

/** The column the built-in manager no-overlap rule uses, or `null` without manager data. */
export function managerColumn(session: MatchingSessionState): string | null {
  return (
    MANAGER_COLUMNS.find(k => session.columns.some(c => c.key === k)) ?? null
  );
}

/**
 * The config for a session nobody has edited yet (`snapshot.config === null`):
 * no overrides, coaches as the session has them, the session's group sizes,
 * every requirement enforced (coach capacity only with coach data), the
 * built-in manager no-overlap rule, no goals.
 */
export function defaultConfig(
  session: MatchingSessionState,
  requestNumber = 1
): MatchingConfig {
  const sizes =
    session.defaultSizes.length > 0 ? session.defaultSizes : [4, 5, 6];
  const manager = managerColumn(session);
  const columnRules: ColumnRule[] = manager
    ? [{ column: manager, type: 'no_overlap', enabled: true, builtin: true }]
    : [];
  return {
    version: 1,
    participants: { availability: {}, doNotMatch: emptyDoNotMatch() },
    doNotMatchGroups: [{ name: 'List 1', emails: [] }],
    coaches: Object.fromEntries(
      session.coaches.map(c => [
        c.id,
        { availableSlots: c.sourceSlots, maxGroups: c.sourceMaxGroups },
      ])
    ),
    groupSize: { min: Math.min(...sizes), max: Math.max(...sizes) },
    enforce: {
      doNotMatch: true,
      coachCapacity: hasCoachData(session),
      availability: true,
    },
    columnRules,
    goals: [],
    request: { name: `Request ${requestNumber}`, note: '' },
  };
}
